<template>
  <!-- Landing Step: Hero + Content -->
  <template v-if="store.currentStep === 'landing'">
    <Hero />
    <StatsStrip />
    <ExampleReports />
    <HowItWorks />
    <TrustBar />
    <CtaBanner @get-report="goToOAuth" />
    <BrandCarousel />
  </template>

  <!-- OAuth Step -->
  <template v-else-if="store.currentStep === 'oauth'">
    <OAuthStep />
  </template>

  <!-- OAuth Loading Step: fetching vehicles after Tesla callback -->
  <template v-else-if="store.currentStep === 'oauth-loading'">
    <div class="oauth-loading-screen">
      <div class="oauth-loading-spinner"></div>
      <p class="oauth-loading-text">{{ t('oauth.loadingVehicles') }}</p>
    </div>
  </template>

  <!-- Package Step -->
  <template v-else-if="store.currentStep === 'package'">
    <div class="container mx-auto px-4 py-8 max-w-3xl">
      <PaymentStep />
    </div>
  </template>

  <!-- Payment Gateway Step -->
  <template v-else-if="store.currentStep === 'payment-gateway'">
    <div class="container mx-auto px-4 py-8 max-w-2xl">
      <PaymentGateway />
    </div>
  </template>

  <!-- Generating Reports Step -->
  <template v-else-if="store.currentStep === 'generating'">
    <div class="container mx-auto px-4 py-8 max-w-xl">
      <GeneratingStep />
    </div>
  </template>

  <!-- Report Step -->
  <template v-else-if="store.currentStep === 'report'">
    <ReportDashboardDefault />
  </template>

  <!-- Support Page -->
  <template v-else-if="store.currentStep === 'support'">
    <SupportPage />
  </template>
</template>

<script setup lang="ts">
/**
 * MainFlow.vue - Store-driven wizard view (spec 047-blog-section, T0.3)
 *
 * Holds the existing connect-first journey that used to live inline in
 * `App.vue`'s `<main>` (landing → brand → OAuth → payment → generating →
 * report, plus the support page). The router mounts this single view for the
 * non-blog surfaces; transitions remain driven by the Pinia step-machine
 * (`store.currentStep`) because a wizard step is non-URL state. App-level
 * chrome (Header / Footer / banners) and the payment/OAuth
 * callback handling stay in `App.vue`.
 */
import { useI18n } from 'vue-i18n';
import { useAppStore } from '@/stores/app';

import Hero from '@/components/Hero.vue';
import StatsStrip from '@/components/StatsStrip.vue';
import PaymentStep from '@/components/PaymentStep.vue';
import PaymentGateway from '@/components/PaymentGateway.vue';
import OAuthStep from '@/components/OAuthStep.vue';
import GeneratingStep from '@/components/GeneratingStep.vue';
import ReportDashboardDefault from '@/components/ReportDashboardDefault.vue';
import SupportPage from '@/components/SupportPage.vue';
import ExampleReports from '@/components/ExampleReports.vue';
import HowItWorks from '@/components/HowItWorks.vue';
import TrustBar from '@/components/TrustBar.vue';
import CtaBanner from '@/components/CtaBanner.vue';
import BrandCarousel from '@/components/BrandCarousel.vue';

const { t } = useI18n();
const store = useAppStore();

const goToOAuth = () => {
  store.setStep('oauth');
};
</script>

<style scoped>
.oauth-loading-screen {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  gap: 1.5rem;
}
.oauth-loading-spinner {
  width: 44px;
  height: 44px;
  border: 3px solid rgba(255, 255, 255, 0.1);
  border-top-color: #b8f400;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}
.oauth-loading-text {
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.95rem;
}
@keyframes spin {
  to { transform: rotate(360deg); }
}
</style>
